// /incidents/new/chat — orchestrator. Routes wired in App.js. ~110 lines.
import React from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../store";
import { useChatState } from "./useChatState";
import { ChatHeader } from "./ChatHeader";
import { ChatMessageList } from "./ChatMessageList";
import { ChatInputBar } from "./ChatInputBar";
import { ChatIncidentPreview } from "./ChatIncidentPreview";
import { ChatConfetti } from "./ChatConfetti";
import { CATEGORY_TO_TEMPLATE_KEY } from "./aiChatConstants";

export default function AiIncidentChat() {
  const navigate = useNavigate();
  const openPanel = useStore((s) => s.openPanel);

  const s = useChatState();

  const handleSend = () => {
    if (s.busy || !s.input.trim() || s.hitTurnLimit) return;
    s.sendTurn(s.input);
  };

  const handleConfirmCreate = () => {
    if (s.busy) return;
    s.sendTurn("yes, create it", { confirm: true });
  };

  const handleEditInWizard = () => {
    navigate("/incidents/new", {
      state: { prefill: s.extracted, fromChat: true },
    });
  };

  const handleOpenCreatedIncident = () => {
    if (!s.createdIncident) return;
    openPanel(s.createdIncident);
    navigate(`/incidents?focus=${s.createdIncident.id}`);
  };

  const handleSwitchToWizard = () => {
    navigate("/incidents/new", {
      state:
        s.extracted.title || s.extracted.category
          ? { prefill: s.extracted, fromChat: true }
          : undefined,
    });
  };

  const handleAcceptSuggestion = () => {
    if (s.busy || !s.activeSuggestion) return;
    s.setActiveSuggestion(null);
    s.sendTurn("Yes, please add it");
  };

  const handleSkipSuggestion = () => {
    s.setActiveSuggestion(null);
  };

  // Iter33: chip click → prefill input + immediately send the message.
  const handleChipClick = (text) => {
    if (s.busy || s.hitTurnLimit) return;
    s.sendTurn(text);
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "#08090d", color: "#f0f2f8" }}
      data-testid="ai-chat-page"
    >
      <ChatHeader
        onExit={() => navigate("/incidents")}
        onSwitch={handleSwitchToWizard}
      />

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-0">
        {/* LEFT: chat column */}
        <div
          className="flex flex-col"
          style={{
            minHeight: "calc(100vh - 64px)",
            borderRight: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <ChatMessageList
            scrollRef={s.scrollRef}
            messages={s.messages}
            busy={s.busy}
            createdIncident={s.createdIncident}
            readyToCreate={s.readyToCreate}
            extracted={s.extracted}
            activeSuggestion={s.activeSuggestion}
            hitTurnLimit={s.hitTurnLimit}
            briefing={s.briefing}
            onAcceptSuggestion={handleAcceptSuggestion}
            onSkipSuggestion={handleSkipSuggestion}
            onConfirmCreate={handleConfirmCreate}
            onEditInWizard={handleEditInWizard}
            onOpenCreatedIncident={handleOpenCreatedIncident}
            onSwitchToWizard={handleSwitchToWizard}
            onChipClick={handleChipClick}
          />

          {!s.createdIncident && !s.hitTurnLimit && (
            <ChatInputBar
              input={s.input}
              setInput={s.setInput}
              busy={s.busy}
              taRef={s.taRef}
              userTurns={s.userTurns}
              onSend={handleSend}
            />
          )}
        </div>

        {/* RIGHT: live preview (desktop only) */}
        <aside
          className="hidden lg:block"
          style={{
            background: "#0a0c12",
            padding: 20,
            position: "sticky",
            top: 64,
            alignSelf: "flex-start",
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
          data-testid="ai-chat-preview"
        >
          <ChatIncidentPreview
            extracted={s.extracted}
            confidence={s.confidence}
            missing={s.missing}
          />
        </aside>
      </div>

      {s.showConfetti && <ChatConfetti />}
    </div>
  );
}

// Re-export the category map so the wizard can read prefill correctly.
export { CATEGORY_TO_TEMPLATE_KEY };
